import { useEffect, useRef, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Button, Input, Stack, Link, Text, Box } from "@chakra-ui/react";
import { supabase, inviteFlowClient } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { apiGet, apiPost } from "@/lib/api";
import { colors } from "@/theme/colors";
import { AuthShell, Field, ErrorNote, SuccessNote, PasswordInput, PasswordRequirements, PasswordStrength } from "@/pages/auth/AuthShell";
import { checkPassword, interpretPasswordError } from "@/lib/passwordPolicy";
import { logWarn } from "@/lib/log";

// Self-serve fresh link for expired/used invites — server emails it, never shows it.
const RemintForm = () => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await apiPost("/onboarding/remint", { email: email.trim() }); } catch (e2) { logWarn("[invite] remint failed:", e2); }
    setBusy(false);
    setSent(true);
  };
  if (sent) {
    return (
      <SuccessNote data-testid="remint-sent-note">
        If that email has a pending invite, a fresh link is on its way. Check your inbox (and spam folder).
      </SuccessNote>
    );
  }
  return (
    <form onSubmit={send}>
      <Stack gap="3" mt="4">
        <Field label="Email me a fresh invite link">
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
            placeholder="you@company.com" autoComplete="email"
            h="48px" bg={colors.white} borderColor={colors.border} borderRadius="10px"
            data-testid="remint-email-input" />
        </Field>
        <Button type="submit" w="full" variant="outline" loading={busy} disabled={!email.trim()} data-testid="remint-submit">
          Send me a fresh link
        </Button>
      </Stack>
    </form>
  );
};

export default function AcceptInvite() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [needsName, setNeedsName] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [blocked, setBlocked] = useState(false);
  // Honesty rule: never silently adopt a mismatched session. When the link's token
  // fails, we record which account (if any) the page is actually talking about.
  const [linkFailed, setLinkFailed] = useState(false);
  const [sessionEmail, setSessionEmail] = useState("");
  // SOURCE OF TRUTH: the whole flow is pinned to ONE session — the one the token
  // minted (isolated in-memory client) or, on the reload fallback, the live global
  // session. Display, status check and password write all use THIS, never whatever
  // the shared browser storage happens to hold at call time (another tab's admin
  // session once answered the status check while the page named the invitee).
  const flowRef = useRef(null); // { client, token }

  // Server-side check: this link may only complete FIRST-TIME onboarding.
  useEffect(() => {
    if (!ready) return;
    apiGet("/onboarding/invite-status", flowRef.current?.token).then((r) => {
      if (!r.can_set_password) setBlocked(true);
      // No signOut here: the isolated session dies with the page, and killing the
      // browser's real (admin) session was both hostile and destroyed forensics.
    }).catch(() => {}); // network hiccup: the server never mints links for established accounts anyway
  }, [ready]);

  // Once the flow session is ready, prefill the name (invited workers already have
  // one) — otherwise ask for it so the profile never falls back to the email.
  useEffect(() => {
    if (!ready) return;
    const flow = flowRef.current;
    if (!flow) return;
    flow.client.auth.getUser(flow.token).then(async ({ data }) => {
      const uid = data?.user?.id;
      if (!uid) return;
      const { data: prof } = await flow.client.from("profiles").select("full_name").eq("id", uid).maybeSingle();
      const existing = (prof?.full_name || "").trim();
      if (existing) setFullName(existing);
      else setNeedsName(true);
    });
  }, [ready]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // Sanitize pasted artifacts (stray backticks/spaces from chat/email copies).
    const tokenHash = (params.get("token_hash") || "").replace(/[^a-zA-Z0-9]/g, "");
    const type = (params.get("type") || "recovery").replace(/[^a-z_]/g, "") || "recovery";
    if (tokenHash) {
      // Verify on the ISOLATED client: the minted session stays in memory only.
      inviteFlowClient.auth.verifyOtp({ type, token_hash: tokenHash }).then(async ({ data, error }) => {
        if (data?.session && !error) {
          flowRef.current = { client: inviteFlowClient, token: data.session.access_token };
          setEmail(data.user?.email || "");
          setReady(true);
          return;
        }
        // Token failed (used / expired). We may only reuse a live session when the
        // SERVER says that session is still mid-onboarding — and we always NAME it.
        const { data: s } = await supabase.auth.getSession();
        const sessEmail = s?.session?.user?.email || "";
        setSessionEmail(sessEmail);
        if (s?.session) {
          try {
            const r = await apiGet("/onboarding/invite-status", s.session.access_token);
            if (r.can_set_password) {
              flowRef.current = { client: supabase, token: s.session.access_token };
              setEmail(sessEmail);
              setReady(true);
              return;
            }
          } catch (e2) { logWarn("[invite] status check after token failure:", e2); }
        }
        setLinkFailed(true);
      });
    } else {
      supabase.auth.getSession().then(({ data }) => {
        if (data.session) {
          flowRef.current = { client: supabase, token: data.session.access_token };
          setEmail(data.session.user?.email || "");
          setReady(true);
        } else setLinkFailed(true);
      });
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (needsName && !fullName.trim()) {
      setError("Please enter your first and last name.");
      return;
    }
    if (!checkPassword(password).valid) {
      setError("Please choose a password that meets all the requirements below.");
      return;
    }
    const flow = flowRef.current;
    if (!flow) return;
    setLoading(true);
    try {
      // Password is set on the FLOW session — it can never land on another account.
      const { error: upErr } = await flow.client.auth.updateUser({ password });
      if (upErr) throw upErr;
      try { await apiPost("/onboarding/invite-complete", {}, flow.token); } catch (e2) { logWarn("[invite] could not mark invite complete:", e2); }
      if (fullName.trim()) {
        const { data } = await flow.client.auth.getUser(flow.token);
        if (data?.user?.id) {
          await flow.client.from("profiles").update({ full_name: fullName.trim() }).eq("id", data.user.id);
        }
      }
      // Establish a clean, durable session with the new credentials so the
      // user stays signed in (the in-memory flow session dies with this page).
      if (email) {
        try { await signIn(email, password); } catch (e2) { logWarn("[invite] auto sign-in after password set failed:", e2); }
      }
      setDone(true);
      setTimeout(() => navigate("/", { replace: true }), 1200);
    } catch (err) {
      setError(interpretPasswordError(err));
    } finally {
      setLoading(false);
    }
  };

  const signOutHere = async () => {
    await supabase.auth.signOut().catch(() => {});
    setSessionEmail("");
  };

  return (
    <AuthShell
      title="Set your password"
      subtitle="Welcome to Hakina Safety! Choose a password to finish activating your account."
    >
      {done ? (
        <SuccessNote>Password set — signing you in…</SuccessNote>
      ) : blocked ? (
        <>
          <ErrorNote data-testid="blocked-note">
            {email ? <>The account <b>{email}</b> has already finished setting up.</> : "This account has already finished setting up."}
            {" "}Please sign in, or use "Forgot password" if needed.
          </ErrorNote>
          <Link as={RouterLink} to="/login" color={colors.tealHover} fontWeight="600" display="block" mt="4" textAlign="center" data-testid="blocked-signin-link">
            Go to sign in
          </Link>
          <Link as={RouterLink} to="/forgot-password" color={colors.tealHover} fontWeight="600" display="block" mt="2" textAlign="center" data-testid="blocked-forgot-link">
            Forgot password
          </Link>
        </>
      ) : linkFailed ? (
        <>
          <ErrorNote data-testid="link-failed-note">
            This invite link is invalid, already used, or expired.
          </ErrorNote>
          {sessionEmail && (
            <Box mt="3" p="3" borderRadius="10px" bg={colors.cream} data-testid="session-mismatch-note">
              <Text fontSize="sm" color={colors.textPrimary}>
                You're currently signed in as <b>{sessionEmail}</b> — this may not be the account
                this invite was for.
              </Text>
              <Button size="sm" variant="outline" mt="2" onClick={signOutHere} data-testid="mismatch-signout-btn">
                Sign out of {sessionEmail}
              </Button>
            </Box>
          )}
          <RemintForm />
          <Link as={RouterLink} to="/login" color={colors.tealHover} fontWeight="600" display="block" mt="4" textAlign="center" data-testid="failed-signin-link">
            Go to sign in
          </Link>
        </>
      ) : !ready ? (
        <Text fontSize="sm" color={colors.textSecondary} textAlign="center" data-testid="accept-checking">
          Checking your invite link…
        </Text>
      ) : (
        <form onSubmit={submit}>
          <Stack gap="4">
            <ErrorNote>{error}</ErrorNote>
            {email && (
              <Text fontSize="sm" color={colors.textSecondary} data-testid="accept-account-email">
                Setting a password for <b>{email}</b>
              </Text>
            )}
            {needsName && (
              <Field label="Your full name">
                <Input value={fullName} onChange={(e) => setFullName(e.target.value)} required
                  placeholder="First and last name" autoComplete="name"
                  h="48px" bg={colors.white} borderColor={colors.border} borderRadius="10px"
                  data-testid="accept-fullname" />
              </Field>
            )}
            <Field label="Create a password">
              <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} required minLength={10} data-testid="accept-password" />
              <PasswordStrength value={password} />
              <PasswordRequirements value={password} />
            </Field>
            <Button type="submit" w="full" loading={loading} data-testid="accept-submit">
              Set password & continue
            </Button>
          </Stack>
        </form>
      )}
    </AuthShell>
  );
}
